import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api';

const UserEditRoleModal = ({ open, onClose, user, onRoleUpdated, onShowSnackbar }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmationRequired, setConfirmationRequired] = useState(false);

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role || 'project_user');
    }
  }, [user]);

  const getRoleLabel = (role) => {
    return role?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'User';
  };

  const isRoleDemotion = (currentRole, newRole) => {
    const roleHierarchy = {
      'super_admin': 4,
      'project_admin': 3,
      'project_manager': 2,
      'project_user': 1
    };
    return roleHierarchy[newRole] < roleHierarchy[currentRole];
  };

  const handleRoleChange = (newRole) => {
    setSelectedRole(newRole);
    setError(null);
    
    // Check if this is a demotion (requires confirmation)
    if (user && isRoleDemotion(user.role, newRole)) {
      setConfirmationRequired(true);
    } else {
      setConfirmationRequired(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('stratovai_access_token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/${user.id}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: selectedRole })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update role');
      }

      onShowSnackbar('User role updated successfully!', 'success');
      onRoleUpdated();
    } catch (err) {
      setError(err.message);
      onShowSnackbar(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedRole(user?.role || 'project_user');
    setConfirmationRequired(false);
    setError(null);
    onClose();
  };

  if (!user) return null;

  const roleChanged = selectedRole !== user.role;
  const isDemotion = isRoleDemotion(user.role, selectedRole);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Edit User Role</Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            User
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {user.full_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Role
          </Typography>
          <Chip
            label={getRoleLabel(user.role)}
            color={
              user.role === 'super_admin' ? 'error' :
              user.role === 'project_admin' ? 'warning' :
              user.role === 'project_manager' ? 'info' :
              'default'
            }
          />
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>New Role</InputLabel>
          <Select
            value={selectedRole}
            label="New Role"
            onChange={(e) => handleRoleChange(e.target.value)}
            disabled={loading}
          >
            <MenuItem value="project_user">User</MenuItem>
            <MenuItem value="project_manager">Manager</MenuItem>
            <MenuItem value="project_admin">Admin</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </Select>
        </FormControl>

        {roleChanged && isDemotion && confirmationRequired && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Role Demotion
            </Typography>
            <Typography variant="body2">
              You are about to demote <strong>{user.full_name}</strong> from{' '}
              <strong>{getRoleLabel(user.role)}</strong> to{' '}
              <strong>{getRoleLabel(selectedRole)}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This will reduce their permissions. Are you sure you want to continue?
            </Typography>
          </Alert>
        )}

        {roleChanged && !isDemotion && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{user.full_name}</strong> will be promoted to{' '}
              <strong>{getRoleLabel(selectedRole)}</strong> with expanded permissions.
            </Typography>
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Note:</strong> You cannot demote yourself. The last admin in the company cannot be demoted or deactivated.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !roleChanged}
          startIcon={loading && <CircularProgress size={16} />}
          color={isDemotion ? 'warning' : 'primary'}
        >
          {loading ? 'Updating...' : isDemotion ? 'Confirm Demotion' : 'Update Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditRoleModal;

